import { jsxs, jsx } from "react/jsx-runtime";
import { useState, useRef, useReducer, useCallback, useEffect, useLayoutEffect, useMemo } from "react";
import { YMaps, Map } from "@pbe/react-yandex-maps";
import { isTouchDevice } from "@perxis-auto/shared";
import classnames from "../../../../utils/classnames.js";
import FullscreenControl from "./FullscreenControl/FullscreenControl.js";
import GeoObjects from "./GeoObjects.js";
import getObjectCoordinates from "./getObjectCoordinates.js";
import Portal from "./Portal.js";
import ZoomControl from "./ZoomControl/ZoomControl.js";
const YANDEX_MAP_VERSION = "2.1.79";
const isMobile = isTouchDevice();
const DEFAULT_CENTER = [55.76, 37.64];
const DEFAULT_RECTANGLE = [
  [55.991891, 37.214393],
  [55.567576, 38.224995]
];
const DEFAULT_CLUSTER_SIZE = [56, 56];
const DEFAULT_CLUSTER_OFFSET = [-23, -23];
const DEFAULT_PLACEMARK_SIZE = [60, 72];
const DEFAULT_PLACEMARK_OFFSET = [-30, -36];
const MIN_ZOOM = 3;
const DEFAULT_ZOOM = 9;
const SINGLE_PIN_ZOOM = 13;
function useLatest(value) {
  const valueRef = useRef(value);
  useLayoutEffect(() => {
    valueRef.current = value;
  }, [value]);
  return valueRef;
}
const Yandex = ({
  mapsKey,
  language = "ru",
  region = "RU",
  allPins,
  pins,
  iconContentTemplate,
  clusterHoverImage,
  clusterImage,
  clusterSize = DEFAULT_CLUSTER_SIZE,
  clusterHoverSize = clusterSize,
  clusterOffset = DEFAULT_CLUSTER_OFFSET,
  clusterHoverOffset = clusterOffset,
  enableClustering = false,
  selected,
  hovered,
  occupiedAreas,
  placemarkImage,
  placemarkActiveImage,
  placemarkHoverImage,
  placemarkSize = DEFAULT_PLACEMARK_SIZE,
  placemarkHoverSize = placemarkSize,
  placemarkActiveSize = placemarkSize,
  placemarkOffset = DEFAULT_PLACEMARK_OFFSET,
  placemarkHoverOffset = placemarkOffset,
  placemarkActiveOffset = placemarkOffset,
  enableBalloon = false,
  balloonContent,
  enableHint = false,
  hintContent,
  onSelectPin,
  onHoverPin,
  onMapClick,
  shouldZoomOnClickPin,
  scrollZoom,
  minZoom = MIN_ZOOM,
  maxZoom,
  zoomControl = !isMobile,
  fullscreenControl = isMobile,
  multiTouch,
  zoomControlPosition,
  onPressFullscreen
}) => {
  const [isMapInitialized, setIsMapInitialized] = useState(false);
  const mapRef = useRef(null);
  const latestPins = useLatest(pins);
  const ymap = useRef(null);
  const occupiedAreasAccessors = useRef([]);
  const [_, forceUpdate] = useReducer((v) => v + 1, 0);
  const checkIsSelected = (pinId) => {
    var _a;
    if (!mapRef.current) {
      return false;
    }
    if (typeof pinId === "undefined" || typeof ((_a = mapRef.current) == null ? void 0 : _a.selectedMapPlacemark) === "undefined") {
      return false;
    }
    return pinId === mapRef.current.selectedMapPlacemark;
  };
  const checkIsHovered = (pinId) => {
    if (!mapRef.current) {
      return false;
    }
    if (typeof pinId === "undefined" || typeof mapRef.current.hoveredMapPlacemark === "undefined") {
      return false;
    }
    return pinId === mapRef.current.hoveredMapPlacemark;
  };
  const configureYandexMaps = () => {
    if (!!ymap.current && !iconContentTemplate) {
      iconContentTemplate = ymap.current.templateLayoutFactory.createClass("<div></div>");
      setTimeout(forceUpdate, 100);
    }
  };
  const getRectangle = useCallback(() => {
    if (!ymap.current) {
      return DEFAULT_RECTANGLE;
    }
    const resolvedPins = latestPins.current || allPins;
    const points = resolvedPins.map((p) => [p.latitude, p.longitude]);
    const bounds = ymap.current.util.bounds.fromPoints(points);
    return bounds;
  }, [allPins, latestPins]);
  const getZoom = useCallback(
    (pin) => {
      const rect = getRectangle();
      if (rect[1][0] - rect[0][0] > 1 && rect[1][1] - rect[0][1] > 1) {
        return minZoom;
      }
      if (!pin) {
        return DEFAULT_ZOOM;
      }
      return SINGLE_PIN_ZOOM;
    },
    [getRectangle, minZoom]
  );
  const getDefaultCenterAndZoom = useCallback(() => {
    let zoom = minZoom;
    let mapCenter = DEFAULT_CENTER;
    if (pins.length) {
      zoom = getZoom();
      mapCenter = [pins[0].latitude, pins[0].longitude];
    }
    return {
      zoom,
      center: mapCenter
    };
  }, [getZoom, minZoom, pins]);
  const getBoundsAndZoom = useCallback(() => {
    const resolvedPins = pins.length ? pins : allPins;
    const points = resolvedPins.map((p) => [p.latitude, p.longitude]);
    const bounds = ymap.current.util.bounds.fromPoints(points);
    const zoom = resolvedPins.length === 1 ? SINGLE_PIN_ZOOM : void 0;
    return { bounds, zoom };
  }, [allPins, pins]);
  useEffect(() => {
    if (!isMapInitialized || !mapRef.current) {
      return;
    }
    const accessors = occupiedAreasAccessors.current;
    accessors.forEach((accessor) => accessor.remove());
    if (!occupiedAreas) {
      mapRef.current.margin.setDefaultMargin();
      return;
    }
    occupiedAreasAccessors.current = [];
    occupiedAreas.forEach((area) => {
      const accessor = mapRef.current.margin.addArea(area);
      occupiedAreasAccessors.current.push(accessor);
    });
  }, [isMapInitialized, occupiedAreas]);
  const handleMouseOutPin = useCallback(
    (e) => {
      const geoObject = e.get("target");
      const objectCoordinates = geoObject.geometry.getCoordinates();
      const coordinates = getObjectCoordinates(geoObject, objectCoordinates, mapRef.current);
      onHoverPin == null ? void 0 : onHoverPin({
        id: null,
        coordinates,
        domEvent: e.originalEvent.domEvent.originalEvent
      });
    },
    [onHoverPin]
  );
  const handleMouseInPin = useCallback(
    (e) => {
      const geoObject = e.get("target");
      const objectCoordinates = geoObject.geometry.getCoordinates();
      const pinId = geoObject.properties.get("pinId");
      const isSelected = checkIsSelected(pinId);
      const coordinates = getObjectCoordinates(geoObject, objectCoordinates, mapRef.current);
      if (!isSelected) {
        onHoverPin == null ? void 0 : onHoverPin({
          id: pinId,
          coordinates,
          domEvent: e.originalEvent.domEvent.originalEvent
        });
      }
    },
    [onHoverPin]
  );
  const handleMouseOutCluster = useCallback(
    (e) => {
      const geoObject = e.get("target");
      geoObject.options.set("clusterIcons", [
        {
          href: clusterImage,
          size: clusterSize,
          offset: clusterOffset
        }
      ]);
      geoObject.options.set(
        "clusterIconContentLayout",
        !!ymap.current && ymap.current.templateLayoutFactory.createClass(
          `<div class="${classnames("perxis-auto-map__cluster")}">$[properties.geoObjects.length]</div>`
        )
      );
    },
    [clusterImage, clusterOffset, clusterSize]
  );
  const handleMouseInCluster = useCallback(
    (e) => {
      const geoObject = e.get("target");
      geoObject.options.set("clusterIcons", [
        {
          href: clusterHoverImage,
          size: clusterHoverSize,
          offset: clusterHoverOffset
        }
      ]);
      geoObject.options.set(
        "clusterIconContentLayout",
        !!ymap.current && ymap.current.templateLayoutFactory.createClass(
          `<div class="${classnames([
            "perxis-auto-map__cluster",
            "perxis-auto-map__cluster-hover"
          ])}">$[properties.geoObjects.length]</div>`
        )
      );
    },
    [clusterHoverImage, clusterHoverOffset, clusterHoverSize]
  );
  const setCenter = useCallback(
    (pin) => {
      if (!pin || !mapRef.current) {
        return;
      }
      setTimeout(() => {
        var _a;
        (_a = mapRef.current) == null ? void 0 : _a.setCenter([pin.latitude, pin.longitude], getZoom(pin));
      }, 10);
    },
    [getZoom]
  );
  const handleClickPin = useCallback(
    (pin) => {
      const isSelected = checkIsSelected(pin == null ? void 0 : pin.id);
      onSelectPin == null ? void 0 : onSelectPin(isSelected ? null : pin.id);
      if (shouldZoomOnClickPin) {
        setCenter(pin);
      }
    },
    [onSelectPin, setCenter, shouldZoomOnClickPin]
  );
  const updatePins = useCallback(() => {
    const setPinImage = (placemark) => {
      const pinId = placemark.properties.get("pinId");
      const isSelected = checkIsSelected(pinId);
      const isHovered = checkIsHovered(pinId);
      if (isSelected && isHovered) {
        placemark.options.set("iconImageHref", placemarkActiveImage);
        placemark.options.set("iconImageSize", placemarkActiveSize);
        placemark.options.set("iconImageOffset", placemarkActiveOffset);
      }
      if (isSelected) {
        placemark.options.set("iconImageHref", placemarkActiveImage);
        placemark.options.set("iconImageSize", placemarkActiveSize);
        placemark.options.set("iconImageOffset", placemarkActiveOffset);
        return;
      }
      if (isHovered) {
        placemark.options.set("iconImageHref", placemarkHoverImage);
        placemark.options.set("iconImageSize", placemarkHoverSize);
        placemark.options.set("iconImageOffset", placemarkHoverOffset);
        return;
      }
      placemark.options.set("iconImageHref", placemarkImage);
    };
    if (mapRef.current) {
      setTimeout(() => {
        var _a;
        (_a = mapRef.current) == null ? void 0 : _a.geoObjects.each((geoObject) => {
          if ("getGeoObjects" in geoObject) {
            geoObject.getGeoObjects().forEach(setPinImage);
          } else {
            setPinImage(geoObject);
          }
        });
      }, 10);
    }
  }, [
    placemarkActiveImage,
    placemarkHoverImage,
    placemarkImage,
    placemarkActiveSize,
    placemarkActiveOffset,
    placemarkHoverSize,
    placemarkHoverOffset
  ]);
  const setCenterAndZoom = useCallback(() => {
    if (!mapRef.current) {
      return;
    }
    setTimeout(() => {
      const { bounds, zoom } = getBoundsAndZoom();
      mapRef.current.setBounds(bounds, { useMapMargin: true });
      if (zoom) {
        mapRef.current.setZoom(zoom, { useMapMargin: true });
      }
    }, 10);
  }, [getBoundsAndZoom]);
  useEffect(() => {
    if (!mapRef.current) {
      return;
    }
    mapRef.current.hoveredMapPlacemark = hovered || void 0;
    updatePins();
  }, [hovered, updatePins]);
  useEffect(() => {
    if (!mapRef.current) {
      return;
    }
    mapRef.current.selectedMapPlacemark = selected || void 0;
    updatePins();
  }, [selected, updatePins]);
  useLayoutEffect(() => {
    if (!isMapInitialized) {
      return;
    }
    setCenterAndZoom();
    updatePins();
  }, [isMapInitialized, updatePins, setCenterAndZoom]);
  useEffect(() => {
    if (!isMapInitialized || !mapRef.current || !onMapClick) {
      return;
    }
    mapRef.current.events.add("click", (e) => {
      const geoObject = e.get("target");
      const clickCoordinates = e.get("coords");
      const coordinates = getObjectCoordinates(geoObject, clickCoordinates, mapRef.current);
      onMapClick(coordinates);
    });
  }, [isMapInitialized, onMapClick]);
  const memoPins = useMemo(() => pins, [pins]);
  const hiddenBalloon = typeof selected === "undefined" || !balloonContent || !enableBalloon;
  const hiddenHint = typeof hovered === "undefined" || !hintContent || !enableHint;
  return /* @__PURE__ */ jsxs(
    YMaps,
    {
      version: YANDEX_MAP_VERSION,
      query: { lang: `${language}_${region}`, apikey: mapsKey },
      children: [
        /* @__PURE__ */ jsxs(
          Map,
          {
            width: "100%",
            height: "100%",
            defaultState: getDefaultCenterAndZoom(),
            options: {
              suppressMapOpenBlock: true,
              minZoom,
              maxZoom,
              fullscreenZIndex: 0,
              autoFitToViewport: "always"
            },
            modules: [
              "templateLayoutFactory",
              "layout.ImageWithContent",
              "util.bounds",
              "geoObject.addon.balloon",
              "geoObject.addon.hint"
            ],
            onLoad: (ymaps) => {
              if (ymaps && !ymap.current) {
                ymap.current = ymaps;
                configureYandexMaps();
              }
            },
            instanceRef: (ref) => {
              if (ref && !mapRef.current) {
                mapRef.current = ref;
                setIsMapInitialized(true);
                if (scrollZoom) {
                  mapRef.current.behaviors.enable("scrollZoom");
                } else {
                  mapRef.current.behaviors.disable("scrollZoom");
                }
                if (multiTouch) {
                  mapRef.current.behaviors.enable("multiTouch");
                } else {
                  mapRef.current.behaviors.disable("multiTouch");
                }
              }
            },
            children: [
              !!zoomControl && /* @__PURE__ */ jsx(
                ZoomControl,
                {
                  onZoom: () => mapRef.current.setZoom(mapRef.current.getZoom() + 1, { checkZoomRange: true }),
                  onUnZoom: () => mapRef.current.setZoom(mapRef.current.getZoom() - 1, { checkZoomRange: true })
                }
              ),
              !!fullscreenControl && /* @__PURE__ */ jsx(FullscreenControl, { occupiedAreas, onPress: onPressFullscreen }),
              isMapInitialized && /* @__PURE__ */ jsx(
                GeoObjects,
                {
                  ymap,
                  pins: memoPins,
                  enableBalloon,
                  enableHint,
                  enableClustering,
                  placemarkImage,
                  placemarkSize,
                  placemarkOffset,
                  clusterImage,
                  clusterSize,
                  clusterOffset,
                  onClickPin: handleClickPin,
                  onMouseInCluster: handleMouseInCluster,
                  onMouseOutCluster: handleMouseOutCluster,
                  onMouseInPin: handleMouseInPin,
                  onMouseOutPin: handleMouseOutPin
                }
              )
            ]
          }
        ),
        !hiddenBalloon && /* @__PURE__ */ jsx(Portal, { elementId: `balloon-${selected}`, children: balloonContent }),
        !hiddenHint && /* @__PURE__ */ jsx(Portal, { elementId: `hint-${hovered}`, children: hintContent })
      ]
    }
  );
};
export {
  Yandex as default
};
//# sourceMappingURL=Yandex.js.map
